import { Box, Button, Card, Stack, Typography, useColorScheme, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import SoopraLogo from '../../assets/images/soopra-icon.png'

interface LegendItem {
    color: string;
    title: string;
    description: string;
    bullets: string[];
}
const JudgingCriteria: React.FC = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const legend: LegendItem[] = [
    {
        color: theme.palette.primary.main,
        title: "Completion",
        description: "Complete all aspects of your profile, content quality, and persona richness",
        bullets: [
            "Detailed profile information",
            "High-Quality Content",
            "voice and Journal Setup"
        ]
    },
    {
      color: theme.palette["Schemes"]["Inverse Primary"],
      title: "Engagement",
      description: 'Interaction and response  quality',
      bullets: [
        "Quality interactions",
        "High engagements",
        "Validated replies",
      ],
    },
    {
      color: theme.palette["Schemes"]["Secondary"],
      title: "Training Streak",
      description: 'Profile depth, content quality, and persona richness',
      bullets: [
        "Daily training consistency",
        "Rich, diverse prompts",
        "Ongoing content depth",
      ],
    },
    {
      color: theme.palette["Schemes"]["Secondary Container"],
      title: "Followers",
      description: 'Interaction and response  quality',
      bullets: [
        "Quality interactions",
        "High engagements",
        "Validated replies",
      ],
    },
  ]

  

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        pb: {xs: 3, md: 0},
        minHeight: 'fit-content',
        py: {xs:3, md: 5},
      }}
    >
        <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: 'fit-content',
          px: {xs: 2.5, md: 4},
          gap: {xs: 3, md: 4},
          maxWidth: "lg",
        }}
      >
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                maxWidth: {xs: 1, md: '46.5%'},
                textAlign: 'center'
            }}
        >
            <Typography variant="h4" color='text.primary'>Judging Criteria</Typography>
            <Typography variant="body1" color="text.secondary">How we evaluate and score contest participants</Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: {xs:'column',md:'row'},
            gap: 10,
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {/* Left: Quadrant card */}
          <Box sx={{ display: "flex", justifyContent: isXs ? "center" : "flex-start" }}>
            <QuadrantCard />
          </Box>

          {/* Right: Legend */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: { xs: 3, md: 4 },
            }}
          >
            {legend.map((item, idx) => (
              <LegendBlock key={item.title} item={item} dense={isXs} />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default JudgingCriteria;

const LegendBlock: React.FC<{ item: LegendItem; dense?: boolean }> = ({ item, dense }) => {
  const theme = useTheme();
  return (
    <Stack spacing={1.2} sx={{ minWidth: 0, maxWidth: {xs:1,md:'90%'}, alignItems: {xs:'center', md:'start'}}}>
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: 0.5,
            bgcolor: item.color,
            flex: "0 0 auto",
          }}
        />
        <Typography variant="h5" color="text.primary">
          {item.title}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{textAlign:{xs: 'center', md: 'left'}}}>{item.description}</Typography>
      {/* Bullets */}
      <Stack component="ul" sx={{ mt: 1, pl: 3, color: "text.primary", gap: 0.5 }}>
        {item.bullets.map((b) => (
          <Typography key={b} component="li" variant={dense ? "body2" : "body1"}>
            {b}
          </Typography>
        ))}
      </Stack>
    </Stack>
  );
};

interface Quadrant {
  label: string;
  value: string;
  color: string;
}


const QuadrantCard: React.FC = () => {
  const theme = useTheme();

  const quadrants: Quadrant[] = [
    { label: "Completion", value: "", color: `${theme.palette.primary.main}da` },
    { label: "Engagement", value: "", color: `${theme.palette["Schemes"]["Inverse Primary"]}da` },
    { label: "Training", value: "", color:  `${theme.palette["Schemes"]["Secondary"]}da`},
    { label: "Followers", value: "", color: `${theme.palette["Schemes"]["Secondary Container"]}da`},
  ];

  return (
    <Box
      sx={{
        width: 300,
        height: 300,
        borderRadius: 20,
        overflow: "hidden",
        border: `10px solid ${theme.palette.grey[700]}`,
        position: "relative",
      }}
    >
      {/* Background Logo */}
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 0,
          position: "absolute",
          inset: 0,
        }}
      >
        <img
          src={SoopraLogo}
          alt="Logo"
          style={{
            filter: 'invert(100%)'
          }}
        />
      </Box>

      {/* Quadrants overlay */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: "1fr 1fr",
          position: "absolute",
          inset: 0,
          zIndex: 1,
        }}
      >
        {quadrants.map((q, index) => (
          <Box
            key={index}
            sx={{
              bgcolor: q.color,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            <Typography variant="h6">{q.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};